export const getEffectiveOptions = (childFilter, parentValue) => {
  const map = childFilter?.depends_on?.options_map || {};
  const parentNull = childFilter?.depends_on?.null_label;

  let list;
  const isNullish =
    parentValue == null ||
    parentValue === "" ||
    (parentNull && parentValue === parentNull);

  if (isNullish) {
    list = map[parentNull] ?? map["all"];
  } else {
    list = map[String(parentValue)];
  }

  if (!Array.isArray(list) || list.length === 0) {
    if (childFilter.type === "makemake_selector") {
      return childFilter.options || childFilter.selectorValues || [];
    }
    return childFilter.options || [];
  }
  return list;
};

export const getDefaultForFilter = (filter, effectiveOptions) => {
  if (filter.type === "makemake_selector") {
    if (filter.allow_null) return "all";
    return effectiveOptions[0]?.name ?? "";
  }
  if (filter.type === "generic_selector") {
    if (filter.allow_null && filter.null_label) return filter.null_label;
    return effectiveOptions[0] ?? "";
  }
  return null;
};

export const cascadeReset = (state, parentId, parentValue, filterConfig) => {
  let next = { ...state };
  const dependents = filterConfig.filter(f => f.depends_on?.id === parentId);

  for (const child of dependents) {
    const eff = getEffectiveOptions(child, parentValue);
    const newVal = getDefaultForFilter(child, eff);

    if (next[child.id] !== newVal) {
      next[child.id] = newVal;
    }
    next = cascadeReset(next, child.id, newVal, filterConfig);
  }
  return next;
};

export const initializeFilterDefaults = (filters, formatDateFn) => {
  const defaults = {};
  
  filters.forEach((f) => {
    if (f.type === "generic_selector") {
      if (f.selectorType === "multiple_selector") {
        defaults[f.id] = (f.allow_null && f.null_label) ? [f.null_label] : [];
      } else {
        if (f.allow_null && f.null_label) {
          defaults[f.id] = f.null_label;
        } else if (Array.isArray(f.options) && f.options.length) {
          defaults[f.id] = f.options[0];
        } else {
          defaults[f.id] = "";
        }
      }
    } else if (f.type === "makemake_selector") {
      if (f.allow_null) {
        defaults[f.id] = "all";
      } else if (Array.isArray(f.options) && f.options.length) {
        defaults[f.id] = f.options[0]?.name ?? "";
      } else {
        defaults[f.id] = "";
      }
    } else if (f.type === "date_range" || f.type === "single_date") {
      const today = new Date();
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      const isSingle = f.type === "single_date";
      const defaultRange = isSingle ? today : [monthAgo, today];
      
      defaults[f.id] = Array.isArray(defaultRange)
        ? defaultRange.map(d => formatDateFn(d))
        : formatDateFn(defaultRange);
    }
  });

  return defaults;
};

export const applyCoherentDefaults = (defaults, filters) => {
  let next = { ...defaults };
  for (const f of filters) {
    const val = next[f.id];
    if (val !== undefined) next = cascadeReset(next, f.id, val, filters);
  }
  return next;
};

export const findParentForChild = (value, optionsMap, filter) => {
  for (const [k, arr] of Object.entries(optionsMap)) {
    if (!Array.isArray(arr)) continue;
    const found = arr.some((o) => String(o?.name ?? o) === String(value));
    if (found) return k;
  }
  return null;
};

export const isFilterValueUnset = (value, filter) => {
  return (
    value == null ||
    value === "" ||
    value === "all" ||
    (filter?.allow_null && filter?.null_label && value === filter.null_label)
  );
};

export const calculateCentroid = (opts, fallbackCenter) => {
  if (!opts?.length) return fallbackCenter;
  const lat = opts.reduce((a, o) => a + o.lat, 0) / opts.length;
  const lon = opts.reduce((a, o) => a + o.lon, 0) / opts.length;
  return [lat, lon];
};

export const applyCascadeToValues = (currentValues, id, newVal, filterConfig) => {
  const next = { ...currentValues, [id]: newVal };
  return cascadeReset(next, id, newVal, filterConfig);
};

export const serializeDateRange = (range, formatDateFn) => {
  return Array.isArray(range)
    ? range.map(formatDateFn)
    : formatDateFn(range);
};

export const buildMakeMakeValues = (filterValues, filter, value, filterConfig) => {
  const childId = filter?.id;
  const parentId = filter?.depends_on?.id;
  const optionsMap = filter?.depends_on?.options_map || {};

  if (!childId || !parentId || isFilterValueUnset(value, filter)) {
    return { shouldCascade: true, childId, value };
  }

  const parentVal = findParentForChild(value, optionsMap, filter);
  if (!parentVal) {
    return { shouldCascade: true, childId, value };
  }

  let next = { ...filterValues, [parentId]: parentVal };
  next = cascadeReset(next, parentId, parentVal, filterConfig);
  next = { ...next, [childId]: value };
  return { shouldCascade: false, values: next };
};
